import { parseNumberValue, useURLState } from "lib/hooks/useURLState";
import { SortState, SortStateSchema } from "lib/search/types";
import { isDefined } from "lib/utils";

/**
 * Filter state for the framework buyers tab. Mirrors the legacy buyer-search filter shape so
 * bookmarked URL state keeps parsing; the supplier and location keys are kept for that reason
 * even though the current search no longer applies them.
 */
export type LegacyBuyerTableFilters = {
  signals?: string[];
  buyerName?: string | null;
  buyerGuids?: string[];
  buyerLists?: string[];
  categories?: { categoryIds?: string[]; includeUncategorised?: boolean };
  buyerTypes?: string[];
  countryCodes?: string[];
  hqCountry?: { countries?: string[]; regions?: string[]; selected?: string[] };
  regionIds?: string[];
  supplierIds?: number[];
  supplierSme?: string | null;
};

/**
 * These functions are mainly used in framework pages.
 * Everywhere else uses {@link useFilterableBuyerTableState}.
 */
export const DEFAULT_LEGACY_BUYER_FILTERS: LegacyBuyerTableFilters = {
  supplierSme: "",
};
export const DEFAULT_LEGACY_BUYER_SORT: SortState = { field: "buyer_name", order: "ASC" };

function parseUrlFilterState(
  val?: LegacyBuyerTableFilters,
  defaultFilters?: LegacyBuyerTableFilters,
) {
  if (typeof val !== "object" || val === null) {
    return defaultFilters || DEFAULT_LEGACY_BUYER_FILTERS;
  }

  const filters = val as LegacyBuyerTableFilters;

  return {
    ...filters,
    supplierIds: filters.supplierIds?.map(parseNumberValue).filter(isDefined),
  };
}

export function useLegacyFilterableBuyerTableState(
  defaultFilters: LegacyBuyerTableFilters,
  defaultSort: SortState,
) {
  const [buyerTableFilters, setBuyerTableFilters] = useURLState(
    "buyerFilters",
    defaultFilters,
    (val) => parseUrlFilterState(val as LegacyBuyerTableFilters, defaultFilters),
  );

  const [buyerSort, setBuyerSort] = useURLState("sortState", defaultSort, SortStateSchema);

  return {
    buyerTableFilters: buyerTableFilters,
    setBuyerTableFilters,
    buyerSort: buyerSort,
    setBuyerSort,
  };
}
